import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import { useLoaderData, useSearchParams } from "@remix-run/react";
import { RiSearchLine } from "react-icons/ri";
import { serverOnly$ } from "vite-env-only/macros";
import type { Range } from "~types/api/range.types";

import { ErrorComponent, NotFoundPage } from "~/components/error-boundary";
import { ItemList } from "~/components/item-list";
import { RangeCard } from "~/components/ui/cards/range-card";
import { PageContainer } from "~/components/ui/layout/page-container";
import { PageHeading } from "~/components/ui/page-heading";
import { Text } from "~/components/ui/typography";
import {
  DEFAULT_ITEMS_PER_PAGE_OPTION,
  DEFAULT_RANGES_SORTING_OPTION,
  RANGES_SORTING_OPTIONS,
} from "~/config/item-list";
import { categoriesWithRanges } from "~/config/navigation";
import { getCategoryBySlug } from "~/models/category.server";
import { getCharacteristicRangeFilters, getFilteredBrands } from "~/models/filter.server";
import { getRanges } from "~/models/range.server";
import { getSession } from "~/models/session.server";
import { getActiveFilters } from "~/services/filters";
import { AppError, NotFoundInApiError, NotFoundPageError } from "~/utils/errors";
import { getSeoMeta } from "~/utils/seo";

export const sitemap = serverOnly$(async () => {
  return categoriesWithRanges.map((category) => ({
    loc: `/${category}/gammes`,
    exclude: !category,
  }));
});

export const meta: MetaFunction<typeof loader> = (metaArgs) => {
  const loaderData = metaArgs.data;

  const searchParams = new URLSearchParams(metaArgs.location.search);
  const activeFilters = loaderData
    ? getActiveFilters({
        searchParams,
        brands: loaderData.filteredBrands,
        characteristicFilters: loaderData.characteristicRangeFilters,
      })
    : null;

  const title = `Gammes - ${loaderData?.category?.title}${activeFilters?.characteristics?.length === 1 ? ` ${activeFilters?.characteristics[0]?.title}` : ""}${activeFilters?.brands?.length === 1 ? ` ${activeFilters?.brands[0]?.title}` : ""}${activeFilters?.ranges?.length === 1 ? ` ${activeFilters?.ranges[0]?.brandRange.title}` : ""}`;

  return getSeoMeta(metaArgs, {
    title,
    description: `${title} : découvrez nos gammes dédiés aux professionnels du photovoltaïque. Powr Connect fournisseur en ${loaderData?.category?.title} pour les pros`,
    selfCanonical: false,
    canonicalPath: `${metaArgs.location.pathname}${metaArgs.location.search}`,
  });
};

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  try {
    const categorySlug = params["category"] ?? null;

    if (categorySlug && !categoriesWithRanges.includes(categorySlug)) {
      return redirect(`/${categorySlug}`);
    }

    const category = await getCategoryBySlug({ slug: categorySlug, request }).catch((error) => {
      if (error instanceof NotFoundInApiError) {
        throw new NotFoundPageError(`Aucune catégorie ne correspond à cette url.`);
      }
      throw error;
    });

    const url = new URL(request.url);
    const [session, rangesResponse, filteredBrandsResponse, characteristicRangeFilters] =
      await Promise.all([
        getSession({ request }),
        getRanges({
          brands: url.searchParams.get("marques"),
          filters: url.searchParams.get("filtres"),
          sortParam: url.searchParams.get("sort-by") || DEFAULT_RANGES_SORTING_OPTION,
          search: url.searchParams.get("search"),
          page: Number(url.searchParams.get("page") || 1),
          itemsPerPage: Number(
            url.searchParams.get("items-per-page") || DEFAULT_ITEMS_PER_PAGE_OPTION
          ),
          request,
        }),
        getFilteredBrands({
          categorySlug: categorySlug || null,
          searchParams: url.searchParams,
          request,
        }),
        getCharacteristicRangeFilters({
          searchParams: url.searchParams,
          request,
        }),
      ]);
    const comparator = session.get("comparator") || null;
    const activeContactId = session.get("activeContactId") || null;

    const activeBrandsFilters = url.searchParams.get("marques")?.split("__") || [];

    if (rangesResponse.data.length === 0 && activeBrandsFilters.length === 1) {
      return redirect(`/${categorySlug}?marques=${activeBrandsFilters[0]}`);
    }

    return json({
      category,
      ranges: rangesResponse.data,
      totalItems: rangesResponse.totalItems,
      filteredBrands: filteredBrandsResponse.data,
      characteristicRangeFilters,
      comparator,
      activeContactId,
      errors: null,
    });
  } catch (error) {
    if (error instanceof AppError) {
      return json({
        category: null,
        ranges: null,
        totalItems: null,
        filteredBrands: null,
        characteristicRangeFilters: null,
        comparator: null,
        activeContactId: null,
        errors: error.getAllErrors(),
      });
    }
    throw error;
  }
};

const RangesPage = () => {
  const data = useLoaderData<typeof loader>();
  const [searchParams] = useSearchParams();

  const {
    category,
    ranges,
    totalItems,
    characteristicRangeFilters,
    filteredBrands,
    comparator,
    activeContactId,
  } = data;

  // Breadcrumbs
  const breadcrumbData = [
    {
      name: category?.title || "",
      slug: category?.slug ? `/${category.slug}` : "",
    },
    { name: "Gammes", slug: "" },
  ];

  const activeFilters = getActiveFilters({
    searchParams,
    brands: filteredBrands,
    characteristicFilters: characteristicRangeFilters,
  });

  const title = `Gammes - ${category?.title}${activeFilters?.characteristics?.length === 1 ? ` - ${activeFilters?.characteristics[0]?.title}` : ""}${activeFilters?.brands?.length === 1 ? ` - ${activeFilters?.brands[0]?.title}` : ""}`;

  return (
    <PageContainer>
      <PageHeading
        breadcrumbData={breadcrumbData}
        HeadingIconComponent={<RiSearchLine />}
        headingText={title || ""}
      />
      <ItemList
        category={category}
        characteristicFilters={characteristicRangeFilters}
        brands={filteredBrands}
        totalItems={totalItems || 0}
        totalItemsText={`Nombre de gammes : ${totalItems || 0}`}
        sortingOptions={RANGES_SORTING_OPTIONS}
        defaultSortingOption={DEFAULT_RANGES_SORTING_OPTION}
        comparator={comparator}
        isUserConnected={!!activeContactId}
        isEmpty={!ranges || ranges.length === 0}
      >
        <>
          {ranges && ranges.length > 0 ? (
            ranges.map((range: Range) => {
              return (
                <RangeCard
                  key={`${range.id}-${range.slug}`}
                  range={range}
                  comparator={comparator}
                  isUserConnected={!!activeContactId}
                />
              );
            })
          ) : (
            <Text muted className="mx-auto mb-8 text-center">
              Aucune gamme n&apos;a été trouvé.
              <br />
              Essayez de modifier vos filtres.
            </Text>
          )}
        </>
      </ItemList>
    </PageContainer>
  );
};

export default RangesPage;

export const ErrorBoundary = () => {
  return (
    <ErrorComponent
      statusHandlers={{
        404: ({ error }) => <NotFoundPage>{error.data}</NotFoundPage>,
      }}
    />
  );
};
